// Block K (UI-20): Add user (super_admin only, as before), in the v2 Modal. Every field the old
// form had, the same payload (POST /api/aidey-team/users). A failure is said inside the dialog.
import React, { useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { Button, ChipGroup, Field, Input, Modal, Select } from '../../ui';
import { PERSONAL_PLANS } from './AdminUserPanel';
import { programmeLabel, roleLabel } from './adminLabels';

const EMPTY = {
  name: '', email: '', password: '', role: 'member', plan: 'Trial', personalTier: 'none', status: 'active',
  trialDays: '', reason: '', programmeType: 'none', programmeEndsAt: '', programmeDiscountPercent: '',
};

export default function AdminAddUser({ open, onClose, metadata, grantableProgrammes, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  React.useEffect(() => { if (open) { setForm(EMPTY); setError(''); setBusy(false); } }, [open]);
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const roles = metadata.createUserRoles.length ? metadata.createUserRoles : ['member', 'admin', 'super_admin', 'cs_manager', 'cs_agent'];
  const plans = metadata.editablePlans.length ? metadata.editablePlans : ['Trial', 'Basics', 'Pro', 'Team', 'Enterprise'];
  const statuses = metadata.accountStatuses.length ? metadata.accountStatuses : ['active', 'suspended', 'closed'];

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const payload = {
        name: form.name.trim(), email: form.email.trim(), role: form.role, plan: form.plan,
        personalTier: form.personalTier, status: form.status,
      };
      if (form.password.trim()) payload.password = form.password;
      if (form.trialDays !== '') payload.trialDays = Number(form.trialDays);
      if (form.reason.trim()) payload.reason = form.reason.trim();
      if (form.programmeType && form.programmeType !== 'none') {
        payload.programmeType = form.programmeType;
        if (form.programmeEndsAt) payload.programmeEndsAt = new Date(form.programmeEndsAt).toISOString();
        if (form.programmeDiscountPercent !== '') payload.programmeDiscountPercent = Number(form.programmeDiscountPercent);
      }
      const res = await api('/api/aidey-team/users', { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data?.user?.id) throw new Error('The server did not confirm the new account. Check the user list before trying again.');
      await onCreated(data);
    } catch (err) {
      setError(messageFromError(err, 'The user was not created.'));
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Add user" color="pastel-blue" width={640}
      footer={<>
        <Button variant="tertiary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="confirm" type="submit" form="adm-add-form" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Creating…' : 'Create user'}</Button>
      </>}>
      <form id="adm-add-form" className="adm-form" onSubmit={submit}>
        <Field label="Full name" required><Input value={form.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="off" /></Field>
        <Field label="Email" required><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} required autoComplete="off" /></Field>
        <Field label="Role">
          <Select value={form.role} onChange={(e) => set({ role: e.target.value })}>{roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}</Select>
        </Field>
        <Field label="Access level (plan)">
          <Select value={form.plan} onChange={(e) => set({ plan: e.target.value })}>{plans.map((p) => <option key={p} value={p}>{p}</option>)}</Select>
        </Field>
        <div className="adm-span"><span className="adm-chip-label" aria-hidden="true">Personal plan</span><ChipGroup label="Personal plan" value={form.personalTier} onChange={(personalTier) => set({ personalTier })} options={PERSONAL_PLANS} /></div>
        <Field label="Status">
          <Select value={form.status} onChange={(e) => set({ status: e.target.value })}>{statuses.map((s) => <option key={s} value={s}>{s}</option>)}</Select>
        </Field>
        <Field label="Temporary password" hint="Leave blank and one is made for you.">
          <Input value={form.password} onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" />
        </Field>
        <Field label="Extend trial by days"><Input type="number" min="0" max="365" inputMode="numeric" value={form.trialDays} onChange={(e) => set({ trialDays: e.target.value })} placeholder="For example, 14" /></Field>
        <Field label="Programme">
          <Select value={form.programmeType} onChange={(e) => set({ programmeType: e.target.value })}>
            <option value="none">None</option>
            {grantableProgrammes.map((t) => <option key={t} value={t}>{programmeLabel(t)}</option>)}
          </Select>
        </Field>
        {(form.programmeType === 'comp_fixed' || form.programmeType === 'trial') && (
          <Field label="Programme ends"><Input type="date" value={form.programmeEndsAt} onChange={(e) => set({ programmeEndsAt: e.target.value })} /></Field>
        )}
        {form.programmeType === 'discount' && (
          <Field label="Discount %"><Input type="number" min="0" max="100" inputMode="numeric" value={form.programmeDiscountPercent} onChange={(e) => set({ programmeDiscountPercent: e.target.value })} placeholder="For example, 25" /></Field>
        )}
        <Field label="Reason or note" className="adm-span"><Input value={form.reason} onChange={(e) => set({ reason: e.target.value })} placeholder="Why this account is being created" /></Field>
        {error && <p className="adm-error adm-span" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
